import { ReduceStore } from "flux/utils";
import invariant from "invariant";
import ContextedDispatcher from "./ContextedDispatcher";
import ContextActions from "./ContextActions";

export const ACTION_CONTEXT_KEY = "__CONTEXT_KEY";

let keyCounter = 0;

export const keyFactory = () =>
    "CTX_" + (++keyCounter);

class ContextedReduceStore extends ReduceStore {

    constructor(dispatcher) {
        super(dispatcher);
        invariant(
            dispatcher instanceof ContextedDispatcher,
            "ContextedReduceStore requires a ContextedDispatcher"
        );
        // fast-fail on Bad Things(tm)
        invariant(
            this.reduce === ReduceStore.prototype.reduce,
            "ContextedReduceStore subtypes may not override 'reduce'",
        );
        invariant(
            this.getInitialState === ContextedReduceStore.prototype.getInitialState,
            "ContextedReduceStore subtypes may not override 'getInitialState'",
        );
        invariant(
            this.getState === ContextedReduceStore.prototype.getState,
            "ContextedReduceStore subtypes may not override 'getState'",
        );
        const reduceAll = this.reduceEachContext !== ContextedReduceStore.prototype.reduceEachContext;
        // "final" reduce
        this.reduce = (state, action) => {
            const targetCtxName = action[ACTION_CONTEXT_KEY];
            if (targetCtxName != null) {
                const prev = state.hasOwnProperty(targetCtxName)
                    ? state[targetCtxName]
                    : this.getInitialContext();
                const next = this.reduceContext(prev, action);
                if (next !== prev) {
                    state = {
                        ...state,
                        [targetCtxName]: next,
                    };
                }
            }
            if (reduceAll) {
                for (const ctx in state) {
                    if (!state.hasOwnProperty(ctx)) continue;
                    const prev = state[ctx];
                    const next = this.reduceEachContext(prev, action, ctx);
                    if (prev !== next) {
                        state = {
                            ...state,
                            [ctx]: next,
                        };
                    }
                }
            }
            if (action.type === ContextActions.CLOSE) {
                /*
                Blow it away, but it's important that the action is still
                run through the context reducer, in case resources need to
                be cleaned up or side effects need to be initiated.
                 */
                state = {
                    ...state,
                };
                delete state[targetCtxName];
            }
            return state;
        };
    }

    getInitialState() {
        return {};
    }

    getInitialContext() {
        throw new Error("ContextedReduceStore subtypes must override 'getInitialContext'");
    }

    /*
     I get invoked on the target context for every contexted action. This is the
     one you want.
     */
    reduceContext(context, action) {
        throw new Error("ContextedReduceStore subtypes must override 'reduceContext'");
    }

    /*
     I get invoked on each context, for every non-contexted action. You probably
     don't want this one.
     */
    reduceEachContext(context, action, contextId) {
        return context;
    }

    getState(context) {
        invariant(typeof context === "string", "ContextedReduceStore can only retrieve state by context");
        const s = super.getState();
        return s.hasOwnProperty(context) ? s[context] : this.getInitialContext();
    }

    getEachContext() {
        const s = super.getState();
        return Object.keys(s).map(k => s[k]);
    }

    /*
     I indicate whether there's an active context with the given name. You don't
     want this one either, unless you're looking to fast-fail on a race that
     involves a ContextActions.CLOSE action.
     */
    hasContext(context) {
        return super.getState().hasOwnProperty(context);
    }

    getContextNames() {
        return Object.keys(super.getState());
    }

}

export default ContextedReduceStore;



// WEBPACK FOOTER //
// ./app/data/util/ContextedReduceStore.js